import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { useRealtime } from "@/hooks/useRealtime";

/**
 * KOVA notification bell + panel.
 *
 * Polls /api/notifications every 30s for the unread count and lazy-loads
 * the full list when the panel is opened.  Persistent — reads from the
 * backend, not localStorage — so notifications survive refresh.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const loadCount = async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.unread || 0); }
    catch { /* silently ignore */ }
  };
  const loadList = async () => {
    setLoading(true);
    try { const { data } = await api.get("/notifications?limit=30"); setItems(data); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCount(); const i = setInterval(loadCount, 30_000); return () => clearInterval(i); }, []);
  useEffect(() => { if (open) loadList(); }, [open]);
  useRealtime((ev) => {
    if (ev.type === "notification") {
      loadCount();
      if (open) loadList();
    }
  }, { enabled: true });
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markOne = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    setItems((xs) => xs.map((x) => x.id === n.id ? { ...x, read: true } : x));
    setCount((c) => Math.max(0, c - (n.read ? 0 : 1)));
    if (n.action_url) { setOpen(false); nav(n.action_url); }
  };
  const markAll = async () => { await api.post("/notifications/read-all");
    setItems((xs) => xs.map((x) => ({ ...x, read: true }))); setCount(0); };

  const priorityChip = (p) => ({
    normal:    "bg-white/5 text-[#8A8F98] border-white/10",
    important: "bg-yellow-500/10 text-yellow-300 border-yellow-500/25",
    urgent:    "bg-red-500/10 text-red-300 border-red-500/25",
  }[p] || "bg-white/5 text-[#8A8F98] border-white/10");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="topbar-notifications"
        className="relative w-9 h-9 flex items-center justify-center rounded-[10px] border border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#39FF14]/40 transition-colors"
        title="Notifications"
      >
        <Bell size={14} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#39FF14] text-[#0B0F0C] text-[10px] font-mono font-bold flex items-center justify-center shadow-[0_0_8px_rgba(57,255,20,0.6)]" data-testid="notif-unread-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] w-[380px] max-h-[520px] flex flex-col bg-[#121815] border border-[#3A4640] rounded-[12px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] z-50 overflow-hidden"
          data-testid="notif-panel"
        >
          <div className="px-4 py-3 border-b border-[#3A4640] flex items-center justify-between">
            <div>
              <div className="font-heading font-semibold text-[#F8F9FA]">Notifications</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8A8F98]">{count > 0 ? `${count} unread` : "All caught up"}</div>
            </div>
            {count > 0 && (
              <button
                onClick={markAll}
                className="text-[11px] font-heading uppercase tracking-wider text-[#39FF14] hover:text-[#F8F9FA] inline-flex items-center gap-1"
                data-testid="notif-mark-all-read"
              ><CheckCheck size={12} /> Mark all read</button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading && <div className="p-6 text-xs text-[#8A8F98]">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-4">
                <EmptyState icon={Bell} title="No notifications yet" description="Admin broadcasts and future KOVA AI alerts will show up here." variant="inline" testid="notif-empty" />
              </div>
            )}
            {!loading && items.map((n) => (
              <button
                key={n.id}
                onClick={() => markOne(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#3A4640] last:border-0 hover:bg-[#0B0F0C] transition-colors ${n.read ? "opacity-70" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm text-[#F8F9FA] truncate">{n.title}</div>
                  <span className={`text-[9px] uppercase tracking-wider font-heading font-semibold px-1.5 py-0.5 rounded-full border ${priorityChip(n.priority)}`}>{n.priority}</span>
                </div>
                <div className="text-xs text-[#8A8F98] mt-1 line-clamp-2">{n.message}</div>
                <div className="text-[10px] text-[#8A8F98] mt-1 flex items-center gap-2">
                  <span>{n.sender_name}</span>
                  <span>·</span>
                  <span>{new Date(n.created_at).toLocaleString()}</span>
                  {!n.read && <span className="ml-auto tzat-pulse" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
